import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../index';
import { GameSession, TeamColor, Answer } from '../types';
import { parseAnswersCsv } from '../utils/normalize';

export const SessionSettings: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<GameSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  
  // OP答えマッピング編集用
  const [opMappings, setOpMappings] = useState<Record<string, TeamColor>>({});
  const [newOpAnswer, setNewOpAnswer] = useState('');
  const [newOpTeam, setNewOpTeam] = useState<TeamColor>(TeamColor.RED);
  const [opDialogOpen, setOpDialogOpen] = useState(false);

  // CSV インポート用
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvContent, setCsvContent] = useState('');

  useEffect(() => {
    if (!sessionId) return;

    const fetchSession = async () => {
      try {
        const sessionDoc = await getDoc(doc(db, 'battleRoyale', sessionId));
        if (sessionDoc.exists()) {
          const data = { id: sessionDoc.id, ...sessionDoc.data() } as GameSession;
          setSession(data);
          setOpMappings(data.settings.opAnswerMappings || {});
        }
      } catch (err) {
        console.error('Error fetching session:', err);
        setError('セッション情報の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId]);

  const handleSaveSettings = async () => {
    if (!sessionId || !session) return;

    setSaveLoading(true);
    try {
      await updateDoc(doc(db, 'battleRoyale', sessionId), {
        'settings.opAnswerMappings': opMappings,
        'settings.pointsPerCorrect': session.settings.pointsPerCorrect,
        'settings.tableCount': session.settings.tableCount
      });
      
      setError('');
      alert('設定を保存しました');
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('設定の保存に失敗しました');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleAddOpMapping = () => {
    if (!newOpAnswer.trim()) return;
    
    setOpMappings({
      ...opMappings,
      [newOpAnswer]: newOpTeam
    });
    setNewOpAnswer('');
    setOpDialogOpen(false);
  };

  const handleDeleteOpMapping = (answer: string) => {
    const newMappings = { ...opMappings };
    delete newMappings[answer];
    setOpMappings(newMappings);
  };

  const handleImportCsv = async () => {
    if (!csvContent.trim() || !sessionId) return;

    try {
      const answers = parseAnswersCsv(csvContent);
      
      // Answer型に変換
      const formattedAnswers: Answer[] = answers.map(a => ({
        ...a,
        points: 1 // デフォルトポイント
      }));

      // Firestoreに保存
      await updateDoc(doc(db, 'battleRoyale', sessionId), {
        answers: formattedAnswers
      });

      alert(`${formattedAnswers.length}件の答えをインポートしました`);
      setCsvContent('');
      setCsvDialogOpen(false);
    } catch (err) {
      console.error('CSV import error:', err);
      alert('CSVのインポートに失敗しました。形式を確認してください。');
    }
  };

  if (loading) {
    return <Typography>読み込み中...</Typography>;
  }

  if (!session) {
    return <Typography>セッションが見つかりません</Typography>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">
            セッション設定: {session.name}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate(`/battle-royale/master/${sessionId}`)}
          >
            ダッシュボードに戻る
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* 基本設定 */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              基本設定
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="正解時の獲得ポイント"
                type="number"
                value={session.settings.pointsPerCorrect}
                onChange={(e) => setSession({
                  ...session,
                  settings: { ...session.settings, pointsPerCorrect: Number(e.target.value) }
                })}
              />
              <TextField
                label="テーブル数"
                type="number"
                value={session.settings.tableCount}
                onChange={(e) => setSession({
                  ...session,
                  settings: { ...session.settings, tableCount: Number(e.target.value) }
                })}
              />
            </Box>
          </Grid>

          {/* 答えインポート */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              正解リスト
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                現在の登録数: {session.answers.length}件
              </Typography>
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => setCsvDialogOpen(true)}
              >
                CSVインポート
              </Button>
            </Box>
          </Grid>

          {/* OPマッピング */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                OP答え → チーム色マッピング
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setOpDialogOpen(true)}
              >
                追加
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>OP答え</TableCell>
                    <TableCell>チーム色</TableCell>
                    <TableCell width={100}>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(opMappings).map(([answer, team]) => (
                    <TableRow key={answer}>
                      <TableCell>{answer}</TableCell>
                      <TableCell>
                        <Chip 
                          label={team}
                          sx={{ 
                            bgcolor: team,
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteOpMapping(answer)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* 保存ボタン */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSaveSettings}
                disabled={saveLoading}
              >
                設定を保存
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* OPマッピング追加ダイアログ */}
      <Dialog open={opDialogOpen} onClose={() => setOpDialogOpen(false)}>
        <DialogTitle>OP答えマッピングを追加</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="OP答え"
              value={newOpAnswer}
              onChange={(e) => setNewOpAnswer(e.target.value)}
              fullWidth
            />
            <TextField
              select
              label="チーム色"
              value={newOpTeam}
              onChange={(e) => setNewOpTeam(e.target.value as TeamColor)}
              fullWidth
              SelectProps={{
                native: true,
              }}
            >
              {Object.values(TeamColor).map(color => (
                <option key={color} value={color}>{color}</option>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleAddOpMapping} variant="contained">追加</Button>
        </DialogActions>
      </Dialog>

      {/* CSVインポートダイアログ */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>正解CSVインポート</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            形式: id,canonical,synonym1,synonym2,... (1行目はヘッダー)
          </Typography>
          <TextField
            multiline
            rows={10}
            value={csvContent}
            onChange={(e) => setCsvContent(e.target.value)}
            fullWidth
            placeholder={`id,正解,同義語1,同義語2
1,部分分数分解,ぶぶんぶんすうぶんかい
2,バッグクロージャー,バッグクロージャ,パンの留め具`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleImportCsv} variant="contained">インポート</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};